import { ReactNode, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { baseEndpoint as themeCacheKey } from '../../api/ThemeApi';
import { baseEndpoint as productCacheKey } from '../../api/ProductApi';
import { ProductType, StoreTopAnchorId } from '../../components/store/constants';
import type { FormFields } from '../../components/page/page.schema';
import useSWR from 'swr';
import {
  AvailableProducts,
  BackgroundTheme,
  PageTheme,
  FeaturedThemeSet,
  GroupTheme,
  HeaderTheme,
  NavbarTheme,
  ReferralTheme,
} from 'referral-types';
import { productApi, themeApi } from '../../api/service';
import { scrollToElement } from '../../utils';
import {
  getBackgroundThemeRevisionFromTheme,
  getPageThemeRevisionFromTheme,
  getGroupThemeRevisionFromTheme,
  getHeaderThemeRevisionFromTheme,
  getNavbarThemeRevisionFromTheme,
  getReferralThemeRevisionFromTheme,
} from '../../components/theme/constants';
import { StoreContext } from './StoreThemeContext';
import { ThemeProvider } from '../theme/ThemeProvider';

export const StoreThemeProvider = (props: Props) => {
  const { children } = props;
  const [selectedCategory, setSelectedCategory] = useState<string>(ProductType.background);
  const prevSelectedCategoryRef = useRef<string>(ProductType.background);

  const [storeRevision, setStoreRevision] = useState<FormFields>();

  const [selectedBackgroundTheme, setSelectedBackgroundThemeState] = useState<BackgroundTheme>();
  const [selectedPageTheme, setSelectedPageThemeState] = useState<PageTheme>();
  const [selectedGroupTheme, setSelectedGroupThemeState] = useState<GroupTheme>();
  const [selectedReferralTheme, setSelectedReferralThemeState] = useState<ReferralTheme>();
  const [selectedNavbarTheme, setSelectedNavbarThemeState] = useState<NavbarTheme>();
  const [selectedHeaderTheme, setSelectedHeaderThemeState] = useState<HeaderTheme>();

  const [logoOverride, setLogoOverride] = useState<File | null>();
  const [backgroundImageOverride, setBackgroundImageOverride] = useState<File | null>();
  const [backgroundImageMobileOverride, setBackgroundImageMobileOverride] = useState<File | null>();
  const [groupMediaOverride, setGroupMediaOverride] = useState<File | null>();
  const [headerMediaOverride, setHeaderMediaOverride] = useState<File | null>();

  const [featuredThemeIndex, setFeaturedThemeIndex] = useState(0);
  const prevFeaturedThemeIndexRef = useRef(0);

  const { data: featuredThemeSets } = useSWR<Array<FeaturedThemeSet>>(
    [themeCacheKey, '/featured'],
    () => themeApi.getFeaturedThemes(),
    { suspense: true }
  );

  const { data: availableProducts, mutate: mutator } = useSWR<AvailableProducts>(
    [productCacheKey, '/all'],
    () => productApi.getAllProducts(),
    { suspense: true }
  );

  useEffect(() => {
    const isInit = storeRevision == null;
    const isNewFeaturedTheme = prevFeaturedThemeIndexRef.current !== featuredThemeIndex;

    if (featuredThemeSets != null && (isInit || isNewFeaturedTheme)) {
      const defaultTheme = featuredThemeSets[featuredThemeIndex];
      setSelectedBackgroundThemeState(featuredThemeSets[featuredThemeIndex].backgroundTheme);
      setSelectedPageThemeState(featuredThemeSets[featuredThemeIndex].pageTheme);
      setSelectedGroupThemeState(featuredThemeSets[featuredThemeIndex].groupTheme);
      setSelectedReferralThemeState(featuredThemeSets[featuredThemeIndex].referralTheme);
      setSelectedNavbarThemeState(featuredThemeSets[featuredThemeIndex].navbarTheme);
      setSelectedHeaderThemeState(featuredThemeSets[featuredThemeIndex].headerTheme);
      setStoreRevision({
        backgroundThemeRevision: {
          ...getBackgroundThemeRevisionFromTheme(featuredThemeSets[featuredThemeIndex].backgroundTheme),
          ...defaultTheme.backgroundThemeRevisionUpdates,
        },
        pageThemeRevision: {
          ...getPageThemeRevisionFromTheme(featuredThemeSets[featuredThemeIndex].pageTheme),
          ...defaultTheme.pageThemeRevisionUpdates,
        },
        groupThemeRevision: {
          ...getGroupThemeRevisionFromTheme(featuredThemeSets[featuredThemeIndex].groupTheme),
          ...defaultTheme.groupThemeRevisionUpdates,
        },
        referralThemeRevision: {
          ...getReferralThemeRevisionFromTheme(featuredThemeSets[featuredThemeIndex].referralTheme),
          ...defaultTheme.referralThemeRevisionUpdates,
        },
        navbarThemeRevision: {
          ...getNavbarThemeRevisionFromTheme(featuredThemeSets[featuredThemeIndex].navbarTheme),
          ...defaultTheme.navbarThemeRevisionUpdates,
        },
        headerThemeRevision: {
          ...getHeaderThemeRevisionFromTheme(featuredThemeSets[featuredThemeIndex].headerTheme),
          ...defaultTheme.headerThemeRevisionUpdates,
        },
      });
    }

    prevFeaturedThemeIndexRef.current = featuredThemeIndex;
  }, [featuredThemeSets, featuredThemeIndex, storeRevision]);

  // useEffect(() => {
  //   const prevSelectedCategory = prevSelectedCategoryRef.current;

  //   if (prevSelectedCategory !== selectedCategory) {
  //     scrollToElement(StoreTopAnchorId, { noScrollPadding: true });
  //   }

  //   prevSelectedCategoryRef.current = selectedCategory;
  // }, [selectedCategory]);

  const setSelectedBackgroundTheme = useCallback((backgroundTheme: BackgroundTheme) => {
    // toast(`'${backgroundTheme.title}' active, customize settings in the settings dropdown.`, { position: "bottom-left", icon: 'ℹ️' });
    setStoreRevision((prev) => ({
      ...prev,
      backgroundThemeRevision: getBackgroundThemeRevisionFromTheme(backgroundTheme),
    }));
    setSelectedBackgroundThemeState(backgroundTheme);
  }, []);

  const setSelectedPageTheme = useCallback((pageTheme: PageTheme) => {
    // toast(`'${pageTheme.title}' active, customize settings in the settings dropdown.`, { position: "bottom-left", icon: 'ℹ️' });
    setStoreRevision((prev) => ({ ...prev, pageThemeRevision: getPageThemeRevisionFromTheme(pageTheme) }));
    setSelectedPageThemeState(pageTheme);
  }, []);

  const setSelectedGroupTheme = useCallback((groupTheme: GroupTheme) => {
    // toast(`'${groupTheme.title}' active, customize settings in the settings dropdown.`, { position: "bottom-left", icon: 'ℹ️' });
    setStoreRevision((prev) => ({ ...prev, groupThemeRevision: getGroupThemeRevisionFromTheme(groupTheme) }));
    setSelectedGroupThemeState(groupTheme);
  }, []);

  const setSelectedReferralTheme = useCallback((referralTheme: ReferralTheme) => {
    // toast(`'${referralTheme.title}' active, customize settings in the settings dropdown.`, { position: "bottom-left", icon: 'ℹ️' });
    setStoreRevision((prev) => ({ ...prev, referralThemeRevision: getReferralThemeRevisionFromTheme(referralTheme) }));
    setSelectedReferralThemeState(referralTheme);
  }, []);

  const setSelectedNavbarTheme = useCallback((navbarTheme: NavbarTheme) => {
    // toast(`'${navbarTheme.title}' active, customize settings in the settings dropdown.`, { position: "bottom-left", icon: 'ℹ️' });
    setStoreRevision((prev) => ({ ...prev, navbarThemeRevision: getNavbarThemeRevisionFromTheme(navbarTheme) }));
    setSelectedNavbarThemeState(navbarTheme);
  }, []);

  const setSelectedHeaderTheme = useCallback((headerTheme: HeaderTheme) => {
    // toast(`'${headerTheme.title}' active, customize settings in the settings dropdown.`, { position: "bottom-left", icon: 'ℹ️' });
    setStoreRevision((prev) => ({
      ...prev,
      headerThemeRevision: getHeaderThemeRevisionFromTheme(headerTheme),
    }));
    setHeaderMediaOverride(undefined);
    setSelectedHeaderThemeState(headerTheme);
  }, []);

  // useDelay(minimumDelay || 0);

  const incrementFeaturedTheme = useCallback(() => {
    if (featuredThemeSets) {
      setFeaturedThemeIndex((prev) => (prev === featuredThemeSets.length - 1 ? 0 : prev + 1));
    }
  }, [featuredThemeSets]);

  const defaultThemes = useMemo(
    () =>
      featuredThemeSets
        ? {
            backgroundTheme: featuredThemeSets[featuredThemeIndex].backgroundTheme,
            pageTheme: featuredThemeSets[featuredThemeIndex].pageTheme,
            groupTheme: featuredThemeSets[featuredThemeIndex].groupTheme,
            referralTheme: featuredThemeSets[featuredThemeIndex].referralTheme,
            navbarTheme: featuredThemeSets[featuredThemeIndex].navbarTheme,
            headerTheme: featuredThemeSets[featuredThemeIndex].headerTheme,
          }
        : undefined,
    [featuredThemeSets, featuredThemeIndex]
  );

  const contextValue = useMemo(
    () => ({
      incrementFeaturedTheme,
      defaultThemes,

      selectedCategory,
      setSelectedCategory,

      storeRevision,
      setStoreRevision,

      mutator,

      selectedBackgroundTheme,
      setSelectedBackgroundTheme,
      selectedPageTheme,
      setSelectedPageTheme,
      selectedGroupTheme,
      setSelectedGroupTheme,
      selectedReferralTheme,
      setSelectedReferralTheme,
      selectedNavbarTheme,
      setSelectedNavbarTheme,
      selectedHeaderTheme,
      setSelectedHeaderTheme,

      logoOverride,
      setLogoOverride,
      backgroundImageOverride,
      setBackgroundImageOverride,
      backgroundImageMobileOverride,
      setBackgroundImageMobileOverride,
      groupMediaOverride,
      setGroupMediaOverride,
      headerMediaOverride,
      setHeaderMediaOverride,

      availableProducts,
    }),
    [
      incrementFeaturedTheme,
      defaultThemes,
      selectedCategory,
      setSelectedCategory,
      storeRevision,
      setStoreRevision,
      mutator,
      selectedBackgroundTheme,
      setSelectedBackgroundTheme,
      selectedPageTheme,
      setSelectedPageTheme,
      selectedGroupTheme,
      setSelectedGroupTheme,
      selectedReferralTheme,
      setSelectedReferralTheme,
      selectedNavbarTheme,
      setSelectedNavbarTheme,
      selectedHeaderTheme,
      setSelectedHeaderTheme,
      logoOverride,
      setLogoOverride,
      backgroundImageOverride,
      setBackgroundImageOverride,
      backgroundImageMobileOverride,
      setBackgroundImageMobileOverride,
      groupMediaOverride,
      setGroupMediaOverride,
      headerMediaOverride,
      setHeaderMediaOverride,
      availableProducts,
    ]
  );

  return (
    <StoreContext.Provider value={contextValue}>
      <ThemeProvider
        color={selectedPageTheme == null ? 10 : selectedPageTheme.colorId}
        lightMode={storeRevision?.pageThemeRevision?.lightMode}
      >
        {children}
      </ThemeProvider>
    </StoreContext.Provider>
  );
};

type Props = {
  children: ReactNode;
  minimumDelay?: number;
};
